import React, { useState, useEffect } from 'react';
import { losApi, PROGRAM_LABELS } from '../../losApi';
import { toast } from '../../../hooks/use-toast';
import { api, isSpendApprovalError } from '../../api';
import { Loader2, FileText, FileCheck2 } from 'lucide-react';
import { fmt$, ModuleHeader, Fld, SelectFld } from '../shared/formFields';
import { formatDateTime } from '../../kit/time';
import { errorMessage } from '../../errors';

const NOT_SET = 'none';
const REVIEW_OPTIONS = [['reviewed', 'Reviewed'], ['not_reviewed', 'Not reviewed']];
const DEFAULT_SETTINGS = { template: 'general_basic', credit_status: 'not_reviewed', income_status: 'not_reviewed', assets_status: 'not_reviewed', aus: null, appraisal: null };

export function PreApprovalModule({ loan }) {
  const [expiry, setExpiry] = useState('');
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  // The preview shows the same name and colors the generated PDF uses (the tenant's brand).
  const [brand, setBrand] = useState({ name: '', primary: '#070B5F', accent: '#6BA82E' });
  const load = () => losApi.preApprovalHistory(loan.loan_file_id).then(setHistory);
  useEffect(() => { load(); }, [loan.loan_file_id]);
  useEffect(() => {
    api.get('/tenants/mine').then(({ data }) => setBrand((b) => ({
      name: data?.name || b.name,
      primary: data?.colors?.primary || b.primary,
      accent: data?.colors?.accent || b.accent,
    }))).catch(() => {});
  }, []);
  useEffect(() => {
    losApi.preApprovalSettings(loan.loan_file_id).then(setSettings).catch(() => {});
  }, [loan.loan_file_id]);

  // Each select saves on change; the screen shows the saved value only after the server accepts it.
  const update = async (patch) => {
    const next = { ...settings, ...patch };
    setSettings(next);
    try {
      setSettings(await losApi.savePreApprovalSettings(loan.loan_file_id, next));
    } catch (e) {
      setSettings(settings);
      toast({ title: 'Could not save', description: errorMessage(e) });
    }
  };
  const openChecks = [['Credit', settings.credit_status], ['Income', settings.income_status], ['Assets', settings.assets_status]]
    .filter(([, v]) => v !== 'reviewed').map(([k]) => k);

  const doGenerate = async () => {
    setGenerating(true);
    try {
      const res = await losApi.generatePreApproval(loan.loan_file_id, { expiry_date: expiry || null });
      toast({ title: 'Pre-Approval letter generated', description: res.original_filename });
      window.open(losApi.publicPdfUrl(res.public_token), '_blank');
      await load();
    } catch (e) {
      // Ticket 167: the interceptor already toasted the approval message.
      if (!isSpendApprovalError(e)) toast({ title: 'Generation failed', description: e.response?.data?.detail?.substring(0, 200) || String(e) });
    } finally { setGenerating(false); }
  };

  return (
    <div className="space-y-4">
      <ModuleHeader title="Pre-Approval Letter" extraButtons={
        <button
          onClick={doGenerate}
          disabled={generating || openChecks.length > 0}
          data-testid="pre-approval-generate-btn"
          className="text-[12px] font-semibold px-3.5 py-1.5 rounded-lg text-white flex items-center gap-1.5"
          style={{ background: '#070B5F' }}
        >
          {generating ? <Loader2 size={12} className="animate-spin" /> : <FileCheck2 size={12} />}
          {generating ? 'Generating…' : 'Generate & Open PDF'}
        </button>
      } />
      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-8">
          <div className="max-w-2xl mx-auto">
            <div className="text-center pb-4 border-b border-slate-100">
              <div className="font-serif text-xl font-bold uppercase" style={{ color: brand.primary }}>{brand.name || 'Your Lender'}</div>
              <div className="text-[11px] font-bold uppercase tracking-widest" style={{ color: brand.accent }}>Mortgage Pre-Approval Letter</div>
            </div>
            <div className="mt-5 text-[13px] text-slate-700 space-y-3 leading-relaxed">
              <p>To Whom It May Concern,</p>
              <p>
                We are pleased to inform you that <b>{loan.urla?.borrower?.first_name || 'The borrower'} {loan.urla?.borrower?.last_name || ''}</b> has been pre-approved
                for a <b>{PROGRAM_LABELS[loan.loan_program]}</b> mortgage loan in the amount of <b>{fmt$(loan.loan_amount)}</b>, based on the credit and financial
                information provided.
              </p>
              <p>
                Subject property: <b>{loan.property_address?.street || 'TBD'}{loan.property_address?.city ? `, ${loan.property_address.city}` : ''}</b>.
                Purchase price up to <b>{fmt$(loan.purchase_price)}</b>. Note rate approximately <b>{loan.note_rate || '—'}%</b>.
              </p>
              <p>Final approval is subject to a satisfactory appraisal, verified income & assets, clean title, and current market conditions.</p>
              <p className="pt-4">Sincerely,</p>
              <p className="font-semibold">{brand.name || 'Your Lender'} Team</p>
            </div>
          </div>

          {/* History */}
          {history.length > 0 && (
            <div className="mt-8 border-t border-slate-100 pt-6">
              <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-3">Generated PDFs ({history.length})</div>
              <div className="space-y-2">
                {history.map((h) => (
                  <div key={h.pdf_id} className="flex items-center justify-between p-2.5 rounded-lg border border-slate-100" data-testid={`pdf-history-${h.pdf_id}`}>
                    <div className="flex items-center gap-2">
                      <FileText size={13} style={{ color: '#6BA82E' }} />
                      <div>
                        <div className="text-[12px] font-semibold" style={{ color: '#070B5F' }}>{h.original_filename}</div>
                        <div className="text-[10px] text-slate-500">{formatDateTime(h.created_at)}</div>
                      </div>
                    </div>
                    <a href={losApi.publicPdfUrl(h.public_token)} target="_blank" rel="noreferrer"
                       className="text-[11px] font-semibold px-2 py-1 rounded border border-slate-200 hover:bg-slate-50" data-testid={`pdf-open-${h.pdf_id}`}>Open PDF</a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <div className="text-[13px] font-semibold mb-3" style={{ color: '#070B5F' }}>Configuration</div>
          <div className="space-y-3">
            <SelectFld label="Letter Template" value={settings.template} onChange={(v) => update({ template: v })}
              options={[['general_basic', 'General Basic'], ['strong', 'Strong Approval'], ['conditional', 'Conditional']]} />
            <Fld label="Expiry Date" type="date" value={expiry} onChange={setExpiry} testId="pre-approval-expiry" />
            <SelectFld label="Credit" value={settings.credit_status} onChange={(v) => update({ credit_status: v })} options={REVIEW_OPTIONS} />
            <SelectFld label="Income" value={settings.income_status} onChange={(v) => update({ income_status: v })} options={REVIEW_OPTIONS} />
            <SelectFld label="Assets" value={settings.assets_status} onChange={(v) => update({ assets_status: v })} options={REVIEW_OPTIONS} />
            <SelectFld label="AUS Approval (recorded only)" value={settings.aus ?? NOT_SET} onChange={(v) => update({ aus: v === NOT_SET ? null : v })}
              options={[[NOT_SET, 'Not recorded'], ['du_approve', 'DU Approve/Eligible'], ['lp_accept', 'LP Accept'], ['manual', 'Manual UW']]} />
            <SelectFld label="Appraisal Eligibility (recorded only)" value={settings.appraisal ?? NOT_SET} onChange={(v) => update({ appraisal: v === NOT_SET ? null : v })}
              options={[[NOT_SET, 'Not recorded'], ['appraisal_required', 'Required'], ['pw_waiver', 'PW Waiver']]} />
          {openChecks.length > 0 && (
            <div className="text-[11px] text-amber-700" data-testid="pre-approval-gate-note">
              Mark {openChecks.join(', ')} as Reviewed to generate the letter.
            </div>
          )}
          </div>
          <div className="mt-4 p-3 rounded-lg text-[11px] leading-relaxed" style={{ background: 'rgba(107,168,46,0.08)', color: '#3a5717' }}>
            <b>How it works:</b> Click <b>Generate & Open PDF</b> to create a branded pre-approval letter for this loan. The PDF is stored securely and shareable via public URL.
          </div>
        </div>
      </div>
    </div>
  );
}
